import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TaxableSuppliesSchema,
  VatExceptionRowSchema,
  VatListingRowSchema,
  VatPeriodReconciliationSchema,
  VatReconciliationRowSchema,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, postJson } from '../../shared/api/client';
import { ExportButton } from '../../shared/export/ExportButton';
import { recordPath } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { Money, RangeFilter, useReportParams, yearToDate } from './common';

const REPORTS = '/api/v1/tax/reports';
const DETAIL = 'tax.vat_transaction_detail';

function useRange() {
  const session = useSession();
  const report = useReportParams();
  const fallback = yearToDate(businessToday(session.data?.businessTimezone));
  const from = report.get('from') || fallback.from;
  const to = report.get('to') || fallback.to;
  return { report, from, to };
}

/** The document a figure came from, linked where the viewer may open it (FRD-VAT-059). */
function SourceLink({
  source,
}: {
  source: { recordType: string; recordId: string; summary: string | null } | null;
}) {
  const session = useSession();
  if (source === null) return <>—</>;
  const path = recordPath(source.recordType, source.recordId);
  const text = source.summary ?? `${source.recordType} #${source.recordId}`;
  return path !== null && hasCapability(session.data, `${source.recordType}:view`) ? (
    <RecordLink to={path}>{text}</RecordLink>
  ) : (
    <>{text}</>
  );
}

const plain = (value: string) => value.replace(/_/g, ' ');

/**
 * The VAT transaction listing (FRD-VAT-060, VAT-020, VAT-024): every VAT detail in the period with
 * its code, treatment, basis, VAT, tax date, counterparty and source document; zero-rated, exempt
 * and out-of-scope lines are separately identifiable by the treatment filter.
 */
export function VatTransactionsPage() {
  const { report, from, to } = useRange();
  const session = useSession();
  const queryClient = useQueryClient();
  const canAssess = hasCapability(session.data, 'tax.vat_transaction_detail:approve');
  const [assessing, setAssessing] = useState<string | null>(null);
  const [outcome, setOutcome] = useState('recoverable');
  const [reason, setReason] = useState('');
  const [journalId, setJournalId] = useState('');
  const assess = useMutation({
    mutationFn: () =>
      postJson(
        `${REPORTS}/vat-transactions/${assessing ?? ''}/assess`,
        {
          outcome,
          reason: reason.trim(),
          ...(journalId.trim() === '' ? {} : { reclassificationJournalId: journalId.trim() }),
        },
        VatListingRowSchema.partial(),
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setAssessing(null);
      await queryClient.invalidateQueries({ queryKey: ['report', 'vat-transactions'] });
    },
  });
  const query = report.query(['direction', 'treatment', 'registrationState', 'overridden']);
  query.set('from', from);
  query.set('to', to);
  const rows = useQuery({
    queryKey: ['report', 'vat-transactions', query.toString()],
    queryFn: () =>
      getData(`${REPORTS}/vat-transactions?${query.toString()}`, z.array(VatListingRowSchema)),
  });
  const select = (label: string, key: string, options: readonly string[]) => (
    <TextField
      select
      size="small"
      label={label}
      value={report.get(key)}
      sx={{ minWidth: 160 }}
      onChange={(e) => {
        report.set({ [key]: e.target.value });
      }}
    >
      <MenuItem value="">Any</MenuItem>
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {plain(option)}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Stack spacing={3}>
      <PageHeader
        title="VAT transactions"
        description="Every VAT detail in the period, traceable to its source document."
        actions={
          <ExportButton
            recordType={DETAIL}
            href={`${REPORTS}/vat-transactions/export?${query.toString()}`}
          />
        }
      />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
        {select('Direction', 'direction', ['input', 'output'])}
        {select('Treatment', 'treatment', [
          'standard',
          'zero_rated',
          'exempt',
          'out_of_scope',
          'reverse_charge',
          'blocked_non_recoverable',
          'other_recoverable',
          'capital_assets',
        ])}
        {select('Registration state', 'registrationState', ['not_registered', 'registered'])}
        {select('Override', 'overridden', ['true'])}
      </Stack>
      {rows.isPending && <LoadingState label="Loading VAT transactions" />}
      {rows.isError && <ErrorState message={rows.error.message} />}
      {rows.data !== undefined && (
        <Section title={`${from} to ${to}`}>
          {rows.data.length === 0 ? (
            <EmptyState title="No VAT transactions in the period" />
          ) : (
            <Table size="small" aria-label="VAT transactions">
              <TableHead>
                <TableRow>
                  <TableCell>Tax date</TableCell>
                  <TableCell>Direction</TableCell>
                  <TableCell>Tax code</TableCell>
                  <TableCell>Treatment</TableCell>
                  <TableCell align="right">Rate %</TableCell>
                  <TableCell align="right">Taxable basis</TableCell>
                  <TableCell align="right">VAT</TableCell>
                  <TableCell align="right">Recoverable</TableCell>
                  <TableCell>Counterparty</TableCell>
                  <TableCell>Source document</TableCell>
                  <TableCell>Notes</TableCell>
                  {canAssess && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.taxDate}</TableCell>
                    <TableCell>{row.direction}</TableCell>
                    <TableCell>{row.taxCode}</TableCell>
                    <TableCell>{plain(row.treatment)}</TableCell>
                    <TableCell align="right">{row.rateApplied}</TableCell>
                    <Money value={row.taxableBasis} />
                    <Money value={row.vatAmount} />
                    <Money value={row.recoverableAmount} />
                    <TableCell>{row.counterparty ?? '—'}</TableCell>
                    <TableCell>
                      <SourceLink source={row.source} />
                    </TableCell>
                    <TableCell>
                      {[
                        row.registrationState === 'not_registered' ? 'pre-registration' : null,
                        row.preRegistrationStatus === 'pending_recovery'
                          ? 'pending recovery'
                          : null,
                        row.assessmentOutcome == null
                          ? null
                          : `assessed ${plain(row.assessmentOutcome)}: ${row.assessmentReason ?? ''}`,
                        row.overridden ? `overridden: ${row.overrideReason ?? ''}` : null,
                        row.adjustmentReference === null
                          ? null
                          : `adjusts ${row.adjustmentReference}`,
                      ]
                        .filter((note) => note !== null)
                        .join('; ') || '—'}
                    </TableCell>
                    {canAssess && (
                      <TableCell>
                        {row.preRegistrationStatus === 'pending_recovery' && (
                          <Button
                            size="small"
                            onClick={() => {
                              setAssessing(row.id);
                              setReason('');
                              setJournalId('');
                            }}
                          >
                            Assess
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      {assessing !== null && (
        <FormDialog
          open
          title="Assess pre-registration VAT"
          submitLabel="Record assessment"
          canSubmit={reason.trim().length >= 5}
          submitting={assess.isPending}
          error={assess.isError ? assess.error.message : null}
          onClose={() => {
            setAssessing(null);
          }}
          onSubmit={() => {
            assess.mutate();
          }}
        >
          <TextField
            select
            label="Outcome"
            value={outcome}
            onChange={(e) => {
              setOutcome(e.target.value);
            }}
          >
            <MenuItem value="recoverable">Qualifies for recovery</MenuItem>
            <MenuItem value="not_recoverable">Does not qualify</MenuItem>
          </TextField>
          <TextField
            label="Reason"
            required
            multiline
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
          <TextField
            label="Reclassification journal id (optional)"
            value={journalId}
            helperText="The controlled adjustment Finance posted to reclassify it (FRD-VAT-013)"
            onChange={(e) => {
              setJournalId(e.target.value);
            }}
          />
        </FormDialog>
      )}
    </Stack>
  );
}

/** VAT exceptions (FRD-VAT-062): missing, overridden, unresolved or inconsistent VAT data. */
export function VatExceptionsPage() {
  const { report, from, to } = useRange();
  const query = new URLSearchParams({ from, to });
  const rows = useQuery({
    queryKey: ['report', 'vat-exceptions', query.toString()],
    queryFn: () =>
      getData(`${REPORTS}/exceptions?${query.toString()}`, z.array(VatExceptionRowSchema)),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="VAT exceptions"
        description="Missing, overridden, unresolved or inconsistent VAT data in the period."
        actions={
          <ExportButton
            recordType={DETAIL}
            href={`${REPORTS}/exceptions/export?${query.toString()}`}
          />
        }
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
      </Stack>
      {rows.isPending && <LoadingState label="Loading VAT exceptions" />}
      {rows.isError && <ErrorState message={rows.error.message} />}
      {rows.data !== undefined && (
        <Section title={`${from} to ${to}`}>
          {rows.data.length === 0 ? (
            <EmptyState title="No VAT exceptions in the period" />
          ) : (
            <Table size="small" aria-label="VAT exceptions">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Exception</TableCell>
                  <TableCell>Detail</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Journal</TableCell>
                  <TableCell>Source document</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.data.map((row, index) => (
                  <TableRow
                    key={`${row.kind}-${row.detailId ?? row.journalId ?? ''}-${String(index)}`}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                    <TableCell>{plain(row.kind)}</TableCell>
                    <TableCell>{row.message}</TableCell>
                    <TableCell>{row.accountCode ?? '—'}</TableCell>
                    <TableCell>
                      {row.journalId === null ? (
                        '—'
                      ) : (
                        <RecordLink to={`/journals/${row.journalId}`}>
                          {row.journalNo ?? 'Journal'}
                        </RecordLink>
                      )}
                    </TableCell>
                    <TableCell>
                      <SourceLink source={row.source} />
                    </TableCell>
                    <Money value={row.amount} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
    </Stack>
  );
}

/**
 * The VAT control reconciliation (FRD-VAT-048, VAT-061): each account a tax mapping names, its
 * posted movement, the part backed by VAT details and the difference. Recording it keeps the
 * figures as period evidence with Finance's netting journal (FRD-VAT-045; CLR-01).
 */
export function VatReconciliationPage() {
  const { report, from, to } = useRange();
  const session = useSession();
  const queryClient = useQueryClient();
  const canRecord = hasCapability(session.data, 'tax.vat_period_reconciliation:create');
  const canSeeRecorded = hasCapability(session.data, 'tax.vat_period_reconciliation:view');
  const query = new URLSearchParams({ from, to });
  const rows = useQuery({
    queryKey: ['report', 'vat-reconciliation', query.toString()],
    queryFn: () =>
      getData(`${REPORTS}/reconciliation?${query.toString()}`, z.array(VatReconciliationRowSchema)),
  });
  const recorded = useQuery({
    queryKey: ['report', 'vat-period-reconciliations'],
    enabled: canSeeRecorded,
    queryFn: () =>
      getData(`${REPORTS}/period-reconciliations`, z.array(VatPeriodReconciliationSchema)),
  });
  const [recording, setRecording] = useState(false);
  const [journalId, setJournalId] = useState('');
  const [note, setNote] = useState('');
  const record = useMutation({
    mutationFn: () =>
      postJson(
        `${REPORTS}/period-reconciliations`,
        {
          from,
          to,
          ...(journalId.trim() === '' ? {} : { nettingJournalId: journalId.trim() }),
          ...(note.trim() === '' ? {} : { note: note.trim() }),
        },
        VatPeriodReconciliationSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setRecording(false);
      await queryClient.invalidateQueries({ queryKey: ['report', 'vat-period-reconciliations'] });
    },
  });
  const unexplained = (rows.data ?? []).some((row) => row.difference !== '0.00');
  return (
    <Stack spacing={3}>
      <PageHeader
        title="VAT control reconciliation"
        description="VAT account movements against the VAT details that produced them."
        actions={
          <>
            <ExportButton
              recordType={DETAIL}
              href={`${REPORTS}/reconciliation/export?${query.toString()}`}
            />
            {canRecord && (
              <Button
                variant="contained"
                onClick={() => {
                  setRecording(true);
                }}
              >
                Record reconciliation
              </Button>
            )}
          </>
        }
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
      </Stack>
      {rows.isPending && <LoadingState label="Loading the reconciliation" />}
      {rows.isError && <ErrorState message={rows.error.message} />}
      {unexplained && (
        <Alert severity="warning" variant="outlined">
          Some VAT account movement is not backed by VAT details. See VAT exceptions for the
          postings concerned.
        </Alert>
      )}
      {rows.data !== undefined && (
        <Section title={`${from} to ${to}`}>
          {rows.data.length === 0 ? (
            <EmptyState
              title="No VAT accounts are mapped"
              detail="Tax account mappings name the accounts this reconciles."
            />
          ) : (
            <Table size="small" aria-label="VAT control reconciliation">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Purposes</TableCell>
                  <TableCell align="right">GL movement</TableCell>
                  <TableCell align="right">From VAT details</TableCell>
                  <TableCell align="right">Difference</TableCell>
                  <TableCell align="right">Lines</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.data.map((row) => (
                  <TableRow key={row.accountId}>
                    <TableCell>
                      <RecordLink
                        to={`/reports/account-ledger?accountId=${row.accountId}&from=${from}&to=${to}`}
                      >
                        {row.accountCode ?? row.accountId} {row.accountName ?? ''}
                      </RecordLink>
                    </TableCell>
                    <TableCell>{row.purposes.map(plain).join(', ')}</TableCell>
                    <Money value={row.glMovement} />
                    <Money value={row.detailMovement} />
                    <Money value={row.difference} bold={row.difference !== '0.00'} />
                    <TableCell align="right">
                      {row.linesWithDetail} / {row.lines}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      {canSeeRecorded && (
        <Section title="Recorded period reconciliations">
          {recorded.data?.length === 0 && <EmptyState title="None recorded yet" />}
          {recorded.data !== undefined && recorded.data.length > 0 && (
            <Table size="small" aria-label="Recorded period reconciliations">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Recorded</TableCell>
                  <TableCell align="right">Difference</TableCell>
                  <TableCell>Netting journal</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recorded.data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.periodFrom} – {row.periodTo}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.recordedAt.slice(0, 10)}
                    </TableCell>
                    <Money value={row.differenceTotal} />
                    <TableCell>
                      {row.nettingJournalId === null ? (
                        '—'
                      ) : (
                        <RecordLink to={`/journals/${row.nettingJournalId}`}>Journal</RecordLink>
                      )}
                    </TableCell>
                    <TableCell>{row.note ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Section>
      )}
      {recording && (
        <FormDialog
          open
          title={`Record the ${from} – ${to} reconciliation`}
          submitLabel="Record"
          canSubmit
          submitting={record.isPending}
          error={record.isError ? record.error.message : null}
          onClose={() => {
            setRecording(false);
          }}
          onSubmit={() => {
            record.mutate();
          }}
        >
          <TextField
            label="Netting journal id (optional)"
            value={journalId}
            helperText="The controlled adjustment journal Finance posted to net the period (CLR-01)"
            onChange={(e) => {
              setJournalId(e.target.value);
            }}
          />
          <TextField
            label="Note"
            multiline
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </FormDialog>
      )}
    </Stack>
  );
}

/** Cumulative taxable supplies against the configured threshold (FRD-VAT-011). */
export function TaxableSuppliesPage() {
  const { report, from, to } = useRange();
  const query = new URLSearchParams({ from, to });
  const data = useQuery({
    queryKey: ['report', 'taxable-supplies', query.toString()],
    queryFn: () =>
      getData(`${REPORTS}/taxable-supplies?${query.toString()}`, TaxableSuppliesSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Taxable supplies against the registration threshold"
        description="Standard-rated and zero-rated output supplies, cumulatively, by month."
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <RangeFilter
          from={from}
          to={to}
          onChange={(range) => {
            report.set(range);
          }}
        />
      </Stack>
      {data.isPending && <LoadingState label="Loading taxable supplies" />}
      {data.isError && <ErrorState message={data.error.message} />}
      {data.data !== undefined && (
        <>
          {data.data.threshold === null ? (
            <Alert severity="info" variant="outlined">
              No registration threshold is configured (vat.registration_threshold). Finance sets it
              after validating the statutory value; the totals below are shown without comparison.
            </Alert>
          ) : (
            <Alert
              severity={data.data.exceeded === true ? 'warning' : 'success'}
              variant="outlined"
            >
              Cumulative taxable supplies AED {data.data.total} against the configured threshold AED{' '}
              {data.data.threshold}: {data.data.exceeded === true ? 'exceeded' : 'not exceeded'}.
            </Alert>
          )}
          <Section title={`${from} to ${to}`}>
            {data.data.months.length === 0 ? (
              <EmptyState title="No taxable supplies in the period" />
            ) : (
              <Table size="small" aria-label="Taxable supplies by month">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Lines</TableCell>
                    <TableCell align="right">Taxable supplies</TableCell>
                    <TableCell align="right">Cumulative</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.data.months.map((month) => (
                    <TableRow key={month.month}>
                      <TableCell>{month.month}</TableCell>
                      <TableCell align="right">{month.count}</TableCell>
                      <Money value={month.basis} />
                      <Money value={month.cumulative} bold />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>
        </>
      )}
    </Stack>
  );
}
